import React from "react";
import { css, cx } from "@emotion/css";
// eslint-disable-next-line no-restricted-imports -- The sheet needs its own fixed light theme.
import { ThemeProvider } from "@mui/material/styles";
import { createMuiTheme } from "theme";
import type {
  Currency,
  QuoteDocumentAddress,
  QuoteDocumentContent,
  QuoteDocumentCustomerInformation,
  QuoteDocumentPriceContent,
  QuoteDocumentPriceLine,
  QuoteTermsRun,
  QuoteTermsSection,
} from "client/api-v2";
import { QuoteTermsSectionFormat } from "client/api-v2";
import { LightTheme } from "components/Theme/Themes";
import logoOctopusDeploy from "resources/images/svgs/logo_octopus_deploy.svg";
import { quoteDocumentColors, quoteDocumentLayout } from "./quoteDocumentStyles";

export const quoteSheetClassName = "quote-sheet";

const documentTheme = createMuiTheme("light", LightTheme);

const cellPadding = `${quoteDocumentLayout.tableBodyPadding} ${quoteDocumentLayout.cellSidePadding}`;

const printExact = { WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" } as const;

const sheetStyles = css({
  color: quoteDocumentColors.ink,
  fontFamily: '"Inter", Roboto, Helvetica, Arial, sans-serif',
  fontSize: quoteDocumentLayout.bodyFontSize,
  lineHeight: "normal",
  display: "flex",
  flexDirection: "column",
  gap: quoteDocumentLayout.pageGap,

  // Why: `text.less` colours these elements from `var(--primaryText)`, which is near-white in dark
  // mode, and an element rule beats an inherited colour, so the sheet sets the ink on each.
  "& p, & ul, & ol, & li, & h1, & h2, & h3, & h4, & h5, & h6": { color: quoteDocumentColors.ink },
  "& a": { color: quoteDocumentColors.link, textDecoration: "underline" },

  "& table": { width: "100%", borderCollapse: "collapse" },
  "& td, & th": { padding: cellPadding, verticalAlign: "top", textAlign: "left" },
});

const pageStyles = css({
  position: "relative",
  overflow: "hidden",
  backgroundColor: quoteDocumentColors.paper,
  width: quoteDocumentLayout.pageWidth,
  minHeight: quoteDocumentLayout.pageHeight,
  maxWidth: "100%",
  margin: "0 auto",
  padding: `${quoteDocumentLayout.pageMarginBlock} ${quoteDocumentLayout.pageMarginInline}`,
  boxSizing: "border-box",
  boxShadow: `0 1px 4px ${quoteDocumentColors.screenShadow}`,
  "& > *:not([aria-hidden])": { position: "relative", zIndex: 1 },
});

const watermarkStyles = css({
  position: "absolute",
  top: "45%",
  left: 0,
  right: 0,
  zIndex: 0,
  textAlign: "center",
  fontSize: quoteDocumentLayout.watermarkFontSize,
  fontWeight: 900,
  letterSpacing: "9pt",
  color: quoteDocumentColors.watermark,
  opacity: 0.09,
  transform: "rotate(-24deg)",
  pointerEvents: "none",
});

const topBarStyles = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  minHeight: quoteDocumentLayout.topBarHeight,
  "& img": { height: "auto", width: "auto", maxHeight: quoteDocumentLayout.logoHeight, maxWidth: "100%", minWidth: 0 },
});

const titleStyles = css({
  fontSize: quoteDocumentLayout.titleFontSize,
  fontWeight: 900,
  letterSpacing: "0.75pt",
  lineHeight: 1,
  marginLeft: "auto",
});

const bandStyles = css({
  margin: quoteDocumentLayout.headerBandMargin,
  lineHeight: quoteDocumentLayout.bandLineHeight,
  "& td": { padding: `0 ${quoteDocumentLayout.headerBandGap}` },
  "& td:first-of-type": { paddingLeft: 0 },
  "& td:last-of-type": { paddingRight: 0 },
});

const factsCellStyles = css({ width: "1%", whiteSpace: "nowrap" });

const headingStyles = css({
  fontSize: quoteDocumentLayout.headingFontSize,
  fontWeight: 700,
  margin: quoteDocumentLayout.headingMargin,
});

const firstHeadingStyles = css({ marginTop: 0 });

const subheadingStyles = css({
  fontSize: quoteDocumentLayout.subheadingFontSize,
  fontWeight: 700,
  margin: "7.5pt 0 1.5pt",
});

const proseStyles = css({ margin: "3pt 0", lineHeight: quoteDocumentLayout.proseLineHeight });

const introductionStyles = css({ marginBottom: "9pt" });

const bulletListStyles = css({
  // Why: `base.less` sets `list-style-type: none` on every `ul` and `ol`.
  listStyleType: "disc",
  margin: "3pt 0",
  paddingLeft: "13.5pt",
  "& li": { margin: "3pt 0", lineHeight: quoteDocumentLayout.proseLineHeight },
});

const priceTableStyles = css({
  "& th": {
    ...printExact,
    backgroundColor: quoteDocumentColors.tableHeader,
    color: quoteDocumentColors.tableHeaderText,
    fontSize: quoteDocumentLayout.tableHeaderFontSize,
    fontWeight: 700,
    paddingTop: quoteDocumentLayout.tableHeaderPadding,
    paddingBottom: quoteDocumentLayout.tableHeaderPadding,
  },
  "& tbody tr:nth-of-type(even)": { ...printExact, backgroundColor: quoteDocumentColors.stripe },
});

const struckStyles = css({
  textDecoration: "line-through",
  color: quoteDocumentColors.struckPrice,
  marginRight: "2.25pt",
});

const summaryStyles = css({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "18pt",
  marginTop: quoteDocumentLayout.summaryMarginTop,
});

const volumeDiscountStyles = css({ fontWeight: 700, paddingTop: quoteDocumentLayout.summaryPadding });

const totalsStyles = css({
  "&&": { width: quoteDocumentLayout.summaryWidth, flexShrink: 0 },
  "& td": { paddingTop: quoteDocumentLayout.summaryPadding, paddingBottom: quoteDocumentLayout.summaryPadding },
});

const totalsLabelStyles = css({ color: quoteDocumentColors.totalsLabel });

const grandTotalStyles = css({
  fontWeight: 700,
  "& td": { borderTop: `0.75pt solid ${quoteDocumentColors.totalsRule}` },
});

const caveatStyles = css({ fontSize: quoteDocumentLayout.caveatFontSize, color: quoteDocumentColors.caveat });

const formRowStyles = css({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  gap: "36pt",
  marginTop: quoteDocumentLayout.formRowGap,
  "&:first-of-type": { marginTop: "16.5pt" },
});

const questionStyles = css({ width: "48%" });

const formHalfStyles = css({ width: "48%", display: "flex", gap: "6pt", alignItems: "baseline", whiteSpace: "nowrap" });

const fillStyles = css({
  flex: 1,
  minHeight: "13.5pt",
  borderBottom: `0.75pt solid ${quoteDocumentColors.formRule}`,
  alignSelf: "flex-end",
});

const signatureGridStyles = css({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: `${quoteDocumentLayout.formRowGap} ${quoteDocumentLayout.signatureColumnGap}`,
  margin: "13.5pt 0 22.5pt",
});

const signatureLabelStyles = css({
  fontSize: quoteDocumentLayout.formLabelFontSize,
  color: quoteDocumentColors.formLabel,
  marginBottom: quoteDocumentLayout.signatureLabelGap,
});

const signatureLineStyles = css({ position: "relative", borderBottom: `0.75pt solid ${quoteDocumentColors.formRule}` });

const anchorStyles = css({
  position: "absolute",
  left: 0,
  bottom: "2.25pt",
  fontSize: quoteDocumentLayout.anchorFontSize,
  lineHeight: 1,
  color: quoteDocumentColors.paper,
  whiteSpace: "nowrap",
});

const customerSignatureHeadingStyles = css({ marginTop: "30pt" });

const closingStyles = css({ textAlign: "center", fontWeight: 700, marginTop: "30pt" });

// Why: `&&` doubles the class to specificity 0,2,0, so it beats the 0,1,1 `td`/`th` text-align
// rule in `sheetStyles` whatever order emotion injects them in.
const rightStyles = css({ "&&": { textAlign: "right" } });

const boldStyles = css({ fontWeight: 700 });

function formatDocumentMoney(amount: number, currency: Currency): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency, currencyDisplay: "code" }).format(amount);
}

function addressLines(address: QuoteDocumentAddress): string[] {
  const cityAndState = [address.city, address.county, address.state].filter(Boolean).join(", ");
  const locality = [cityAndState, address.postalCode].filter(Boolean).join(" ");

  return [address.address1, address.address2, locality || undefined, address.countryName].filter(
    (line): line is string => Boolean(line)
  );
}

function Watermark({ isDraft }: { isDraft: boolean }) {
  return isDraft ? (
    <div aria-hidden className={watermarkStyles}>
      DRAFT
    </div>
  ) : null;
}

function HeaderBand({ content }: { content: QuoteDocumentContent }) {
  const customer: { text: string; isBold?: boolean }[] = [];
  if (content.billTo) {
    customer.push({ text: content.billTo.legalName, isBold: true });
    if (content.attentionName) {
      customer.push({ text: `Attention: ${content.attentionName}` });
    }
    if (content.billTo.address) {
      addressLines(content.billTo.address).forEach((line) => customer.push({ text: line }));
    }
  }

  if (content.reseller) {
    customer.push({ text: `Reseller: ${content.reseller.legalName}`, isBold: true });
    if (content.reseller.address) {
      addressLines(content.reseller.address).forEach((line) => customer.push({ text: line }));
    }
  }

  const facts: { label: string; value: string }[] = [{ label: "Quote Date", value: content.quoteDateLabel }];
  if (content.validUntilLabel) {
    facts.push({ label: "Valid Until", value: content.validUntilLabel });
  }
  facts.push(
    { label: "Quote Number", value: content.quoteNumber },
    { label: "Payment Term", value: content.paymentTermLabel },
    { label: "Subscription Term", value: content.subscriptionTermLabel },
    { label: "Billing Schedule", value: content.billingScheduleLabel }
  );
  if (content.salesRepresentative) {
    facts.push({ label: "Sales Rep", value: content.salesRepresentative });
  }
  if (content.fulfillmentContactLabel) {
    facts.push({ label: "License To", value: content.fulfillmentContactLabel });
  }

  const seller: string[] = [
    content.seller.legalName,
    `${content.seller.taxIdLabel}: ${content.seller.taxId}`,
    `Phone: ${content.seller.phone}`,
    ...content.seller.addressLines,
  ];

  return (
    <table className={bandStyles}>
      <tbody>
        <tr>
          <td>
            {customer.map((line) => (
              <div key={line.text} className={line.isBold ? boldStyles : undefined}>
                {line.text}
              </div>
            ))}
          </td>
          <td className={factsCellStyles}>
            {facts.map((fact) => (
              <div key={fact.label}>
                <span className={boldStyles}>{`${fact.label}:`}</span> {fact.value}
              </div>
            ))}
          </td>
          <td className={rightStyles}>
            {seller.map((line, index) => (
              <div key={line} className={index === 0 ? boldStyles : undefined}>
                {line}
              </div>
            ))}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function UnitPrice({ line, currency }: { line: QuoteDocumentPriceLine; currency: Currency }) {
  const price = formatDocumentMoney(line.discountedUnitPrice, currency);

  if (line.discountedUnitPrice >= line.listUnitPrice) {
    return <>{price}</>;
  }

  return (
    <>
      <span className={struckStyles}>{formatDocumentMoney(line.listUnitPrice, currency)}</span>
      {price}
    </>
  );
}

function PriceTable({ price, productName }: { price: QuoteDocumentPriceContent; productName: string }) {
  const money = (amount: number) => formatDocumentMoney(amount, price.currency);
  const itemized = price.lines.length > 0;

  const headings = !itemized
    ? ["Product", "Description", "Total"]
    : price.hasLineDiscounts
    ? ["Product", "Description", "Quantity", "Unit Price", "Volume Discount", "Total"]
    : ["Product", "Description", "Quantity", "Unit Price", "Total"];

  return (
    <table className={priceTableStyles}>
      <thead>
        <tr>
          {headings.map((heading, index) => (
            <th key={heading} className={index >= 2 ? rightStyles : undefined}>
              {heading}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {price.lines.map((line) => (
          <tr key={`line-${line.vector}`}>
            <td>{productName}</td>
            <td>{line.description}</td>
            <td className={rightStyles}>{line.quantity.toLocaleString("en-US")}</td>
            <td className={rightStyles}>
              <UnitPrice line={line} currency={price.currency} />
            </td>
            {price.hasLineDiscounts && (
              <td className={rightStyles}>
                {line.discountedUnitPrice < line.listUnitPrice ? `${line.discountPercent}%` : ""}
              </td>
            )}
            <td className={rightStyles}>{money(line.lineTotal)}</td>
          </tr>
        ))}
        {price.fixedFees.map((fee) => (
          <tr key={`fee-${fee.description}`}>
            <td>{productName}</td>
            <td>{fee.description}</td>
            {itemized && (
              <>
                <td />
                <td />
                {price.hasLineDiscounts && <td />}
              </>
            )}
            <td className={rightStyles}>{money(fee.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Summary({ price }: { price: QuoteDocumentPriceContent }) {
  const money = (amount: number) => formatDocumentMoney(amount, price.currency);

  return (
    <div className={summaryStyles}>
      <div className={volumeDiscountStyles}>{price.volumeDiscountStatement}</div>
      <table className={totalsStyles}>
        <tbody>
          <tr>
            <td className={totalsLabelStyles}>Subtotal:</td>
            <td className={rightStyles}>{money(price.subtotal)}</td>
          </tr>
          {price.overallDiscount > 0 && (
            <tr>
              <td className={totalsLabelStyles}>Discount:</td>
              <td className={rightStyles}>{`-${money(price.overallDiscount)}`}</td>
            </tr>
          )}
          <tr className={grandTotalStyles}>
            <td className={totalsLabelStyles}>TOTAL:</td>
            <td className={rightStyles}>{money(price.total)}</td>
          </tr>
          {price.tax && (
            <>
              <tr>
                <td className={totalsLabelStyles}>Estimated tax:</td>
                <td className={rightStyles}>{money(price.tax.estimatedTaxAmount)}</td>
              </tr>
              <tr>
                <td className={totalsLabelStyles}>Estimated total including tax:</td>
                <td className={rightStyles}>{money(price.tax.estimatedTotalIncludingTax)}</td>
              </tr>
              <tr>
                <td colSpan={2} className={caveatStyles}>
                  {price.tax.caveat}
                </td>
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Runs({ runs }: { runs: QuoteTermsRun[] }) {
  return runs.map((run) => {
    if (run.url) {
      return (
        <a key={run.text} href={run.url} target="_blank" rel="noreferrer">
          {run.text}
        </a>
      );
    }

    return run.isBold ? (
      <strong key={run.text}>{run.text}</strong>
    ) : (
      <React.Fragment key={run.text}>{run.text}</React.Fragment>
    );
  });
}

function blockKey(block: QuoteTermsRun[]) {
  return block.map((run) => run.text).join("");
}

function ProseSection({ section }: { section: QuoteTermsSection }) {
  return (
    <>
      {section.printsAsSubsection ? (
        <h3 className={subheadingStyles}>{section.heading}</h3>
      ) : (
        <h2 className={headingStyles}>{section.heading}</h2>
      )}
      {section.blocks.map((block) => (
        <p key={blockKey(block)} className={proseStyles}>
          <Runs runs={block} />
        </p>
      ))}
    </>
  );
}

function BulletSection({ section, isFirst }: { section: QuoteTermsSection; isFirst: boolean }) {
  return (
    <>
      <h2 className={isFirst ? cx(headingStyles, firstHeadingStyles) : headingStyles}>{section.heading}</h2>
      <ul className={bulletListStyles}>
        {section.blocks.map((block) => (
          <li key={blockKey(block)}>
            <Runs runs={block} />
          </li>
        ))}
      </ul>
    </>
  );
}

function Answer({ label, value, ruled = true }: { label: string; value?: string | null; ruled?: boolean }) {
  return (
    <div className={formHalfStyles}>
      <span>{label}</span>
      {ruled && <span className={fillStyles}>{value}</span>}
    </div>
  );
}

function Question({ question, children }: { question: string; children: React.ReactNode }) {
  return (
    <div className={formRowStyles}>
      <div className={questionStyles}>{question}</div>
      {children}
    </div>
  );
}

function CustomerInformation({ information }: { information: QuoteDocumentCustomerInformation }) {
  return (
    <div>
      <Question question="Do you require a Purchase Order number on invoice?">
        <Answer label="Purchase Order #:" value={information.purchaseOrderNumber} />
      </Question>
      <Question question="Do you have a VAT # for your invoice?">
        <Answer label="VAT #:" value={information.taxId} />
      </Question>
      <Question question="Do you have a tax exemption certificate?">
        {information.taxExemptionCertificateId ? (
          <Answer label="Certificate #:" value={information.taxExemptionCertificateId} />
        ) : (
          <Answer label="Yes  /  No" ruled={false} />
        )}
      </Question>
      {information.asksForBillingPortal && (
        <Question question="Do invoices need to be submitted through a procurement or billing portal?">
          <Answer label="Billing Portal Name:" />
        </Question>
      )}
      <div className={formRowStyles}>
        <Answer label="Billing Contact Name:" value={information.billingContactName} />
        <Answer label="Billing Contact Email:" value={information.billingContactEmail} />
      </div>
    </div>
  );
}

const blankSignatureCells = [
  { label: "Signature:", field: "s" },
  { label: "Date Signed:", field: "d" },
  { label: "Name (Print):", field: "n" },
  { label: "Title:", field: "t" },
];

function SignatureGrid({ signer }: { signer: number }) {
  return (
    <div className={signatureGridStyles}>
      {blankSignatureCells.map((cell) => (
        <div key={cell.label}>
          <div className={signatureLabelStyles}>{cell.label}</div>
          <div className={signatureLineStyles}>
            <span className={anchorStyles}>{`\\${cell.field}${signer}\\`}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function RecordedAcceptance({ acceptance }: { acceptance: NonNullable<QuoteDocumentContent["acceptance"]> }) {
  return (
    <div className={signatureGridStyles}>
      {[
        { label: "Accepted By:", value: acceptance.acceptedByName },
        { label: "Company:", value: acceptance.acceptedByCompany },
      ].map((cell) => (
        <div key={cell.label}>
          <div className={signatureLabelStyles}>{cell.label}</div>
          <div>{cell.value}</div>
        </div>
      ))}
    </div>
  );
}

export function QuoteSheet({ content }: { content: QuoteDocumentContent }) {
  return (
    <ThemeProvider theme={documentTheme}>
      <article
        aria-label={`${content.title.toLowerCase()} document`}
        className={`${quoteSheetClassName} ${sheetStyles}`}
      >
        <section className={pageStyles}>
          <Watermark isDraft={content.isDraft} />
          <div className={topBarStyles}>
            <img src={logoOctopusDeploy} alt="Octopus Deploy" />
            <div className={titleStyles}>{content.title}</div>
          </div>

          <HeaderBand content={content} />

          {content.introduction && (
            <>
              <h2 className={cx(headingStyles, firstHeadingStyles)}>Your Octopus Subscription</h2>
              <p className={cx(proseStyles, introductionStyles)}>{content.introduction}</p>
            </>
          )}

          <PriceTable price={content.price} productName={content.productName} />
          <Summary price={content.price} />

          {content.termsSections
            .filter((section) => section.format === QuoteTermsSectionFormat.Paragraphs)
            .map((section) => (
              <ProseSection key={section.heading} section={section} />
            ))}
        </section>

        <section className={pageStyles}>
          <Watermark isDraft={content.isDraft} />
          {content.termsSections
            .filter((section) => section.format === QuoteTermsSectionFormat.Bullets)
            .map((section, index) => (
              <BulletSection key={section.heading} section={section} isFirst={index === 0} />
            ))}

          <h2 className={headingStyles}>Customer Information</h2>
          <CustomerInformation information={content.customerInformation} />

          {content.asksForSignature && (
            <>
              <h2 className={cx(headingStyles, customerSignatureHeadingStyles)}>Customer Signature</h2>
              {content.acceptance ? (
                <RecordedAcceptance acceptance={content.acceptance} />
              ) : (
                <SignatureGrid signer={1} />
              )}

              <h2 className={headingStyles}>Octopus Signature</h2>
              <SignatureGrid signer={2} />
            </>
          )}
          <p className={proseStyles}>{content.acceptanceStatement}</p>

          <div className={closingStyles}>Happy Deployments!</div>
        </section>
      </article>
    </ThemeProvider>
  );
}
